import React, { useState, useEffect } from "react";
import { TOKENS } from "../tokens";
import { Card } from "../components/Card";
import { MonthlyPerformanceReports } from "../components/MonthlyPerformanceReports";
import { RegularizationModal } from "../components/RegularizationModal";
import { SectionLabel } from "../components/SectionLabel";
import { StatusPill } from "../components/StatusPill";
import { PerformanceGauge } from "../components/PerformanceGauge";
import { useApp } from "../context/AppContext";
import { getImageUrl, getBackendOrigin, api } from "../api/client";
import { formatClientDisplayTime } from "../hooks/useClock";
import { is18Plus, validatePhone, validateEmail } from "../utils/validation";
import { WhatsAppPhoneField } from "../components/WhatsAppPhoneField";
import { employeeHasWhatsApp } from "../utils/whatsapp";
import { getEmpStatus, canMarkAbsent, isMarkedAbsentToday } from "../utils/empStatus";
import { ArrowLeft, MapPin, Phone, Heart, Briefcase, Calendar, Award, CheckCircle2, Clock, KeyRound, Eye, EyeOff, ExternalLink, Navigation, Compass, Edit3, Save, X, Upload, Camera, FileText, TrendingUp, Star, Trash2, UserCheck, UserX, AlertCircle } from "lucide-react";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid
} from "recharts";


function formatDisplayDateTime(dateStr) {
  if (!dateStr) return "N/A";
  try {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return dateStr;
    return d.toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true
    });
  } catch (e) {
    return dateStr;
  }
}

function InfoRow({ label, value, color }) {
  return (
    <div style={{
      display: "flex", justifyContent: "space-between", alignItems: "flex-start",
      padding: "10px 0", borderBottom: `1px solid ${TOKENS.border}`,
    }}>
      <span style={{ fontSize: 12, color: TOKENS.muted, flex: "0 0 120px" }}>{label}</span>
      <span style={{ fontSize: 13, color: color || TOKENS.ink, fontWeight: 600, textAlign: "right", flex: 1 }}>{value || "—"}</span>
    </div>
  );
}

function AttachmentUploadBtn({ empId, onDone }) {
  const [show, setShow] = useState(false);
  const [caption, setCaption] = useState("");
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  const handleUpload = async () => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("caption", caption);
      await api.post(`/employees/${empId}/attachments`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setShow(false); setCaption(""); setFile(null);
      if (onDone) onDone();
    } catch (err) { console.error("Attachment upload error:", err); }
    finally { setUploading(false); }
  };

  return (
    <div style={{ display: "inline-block" }}>
      <button onClick={() => setShow(v => !v)} style={{ border: "none", background: `${TOKENS.navyDeep}12`, color: TOKENS.navyDeep, borderRadius: 12, padding: "4px 10px", fontSize: 11, fontWeight: 700, display: "inline-flex", alignItems: "center", gap: 4, cursor: "pointer" }}>
        <Upload size={12} /> Add Attachment
      </button>
      {show && (
        <div style={{ background: TOKENS.cream, borderRadius: 10, padding: 12, marginTop: 8, border: `1px solid ${TOKENS.border}` }}>
          <input type="file" accept="image/*,application/pdf" onChange={e => setFile(e.target.files[0])} style={{ fontSize: 12, marginBottom: 8 }} />
          <input placeholder="Caption" value={caption} onChange={e => setCaption(e.target.value)} style={{ width: "100%", padding: "7px 10px", borderRadius: 8, border: `1.5px solid ${TOKENS.border}`, fontSize: 12, color: TOKENS.ink, outline: "none", background: "#fff", marginBottom: 8 }} />
          <button onClick={handleUpload} disabled={!file || uploading} style={{ width: "100%", background: TOKENS.navyDeep, color: "#fff", border: "none", borderRadius: 8, padding: "8px", fontWeight: 700, fontSize: 12, cursor: file && !uploading ? "pointer" : "not-allowed", opacity: file && !uploading ? 1 : 0.6 }}>
            {uploading ? "Uploading…" : "Upload"}
          </button>
        </div>
      )}
    </div>
  );
}

const SUB_TABS = ["Profile", "GPS Location", "OD History", "Performance", "Work Reports", "Reimbursements", "Incidents"];

export function EmployeeDetailScreen({ empId, onBack }) {
  const { getEmployee, tasks, resetEmployeePassword, refreshTeam, fetchLeaves, updateLeave, markEmployeeAbsent } = useApp();
  const emp = getEmployee(empId);
  const [sub, setSub] = useState("Profile");
  const [workReports, setWorkReports] = useState([]);
  const [loadingReports, setLoadingReports] = useState(false);
  const [showRegModal, setShowRegModal] = useState(false);

  const handleUpdateIncidentStatus = async (id, status) => {
    try {
      await api.put(`/incidents/${id}/status`, { status });
      await refreshTeam();
    } catch (err) {
      console.error("Error updating incident status:", err);
      alert("Failed to update status");
    }
  };

  useEffect(() => {
    if (sub === "Work Reports" && emp) {
      setLoadingReports(true);
      const targetEmpId = emp.employee_id || emp.employeeId || emp.id;
      import("../api/client").then(({ api }) =>
        api.get("/reports", { params: { employeeId: targetEmpId } })
          .then(r => setWorkReports(Array.isArray(r.data) ? r.data : (r.data || [])))
          .catch(() => setWorkReports([]))
          .finally(() => setLoadingReports(false))
      );
    }
  }, [sub, emp?.employee_id, emp?.employeeId, emp?.id]);

  // Edit profile state for Manager
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editError, setEditError] = useState("");
  const [editSuccess, setEditSuccess] = useState("");
  const [editForm, setEditForm] = useState({
    fatherName: "",
    motherName: "",
    dob: "",
    bloodGroup: "",
    phone: "",
    email: "",
    hasWhatsapp: false,
    panNo: "",
    uanNo: "",
    pfNo: "",
    esiNo: "",
    pranNo: "",
    baseSalary: "",
  });

  const [waCheckStatus, setWaCheckStatus] = useState("idle"); // idle | checking | yes | no

  const handleOpenEdit = () => {
    if (!emp) return;
    setEditForm({
      fatherName: emp.fatherName || emp.father_name || "",
      motherName: emp.motherName || emp.mother_name || "",
      dob: emp.dob || "",
      bloodGroup: emp.bloodGroup || emp.blood_group || "",
      phone: emp.phone || "",
      email: emp.email || "",
      hasWhatsapp: !!(emp.hasWhatsapp || emp.has_whatsapp),
      panNo: emp.panNo || emp.pan_no || "",
      uanNo: emp.uanNo || emp.uan_no || "",
      pfNo: emp.pfNo || emp.pf_no || "",
      esiNo: emp.esiNo || emp.esi_no || "",
      pranNo: emp.pranNo || emp.pran_no || "",
      baseSalary: emp.baseSalary || emp.base_salary || "",
    });
    setEditError("");
    setEditSuccess("");
    setWaCheckStatus("idle");
    setIsEditing(v => !v);
  };

  const handleSaveEmpDetails = async (e) => {
    e.preventDefault();
    setEditError(""); setEditSuccess("");

    if (editForm.dob && !is18Plus(editForm.dob)) {
      setEditError("Date of Birth must indicate age 18 or older.");
      return;
    }
    if (editForm.phone && !validatePhone(editForm.phone)) {
      setEditError("Phone number must be exactly 10 digits.");
      return;
    }
    if (editForm.email && !validateEmail(editForm.email)) {
      setEditError("Email address must be valid and end with .com (e.g. name@gmail.com).");
      return;
    }
    if (editForm.phone && waCheckStatus === "checking") {
      setEditError("Still checking whether this mobile has WhatsApp. Please wait a moment.");
      return;
    }
    if (editForm.phone && waCheckStatus === "no") {
      if (!window.confirm("This number does not appear to have WhatsApp (No WA). Save anyway? Daily status will not be sent to this mobile.")) {
        return;
      }
    }

    setSaving(true);
    try {
      await api.patch(`/employees/${emp.employeeId || emp.employee_id || emp.id}`, editForm);
      await refreshTeam();
      setEditSuccess("Profile details saved successfully!");
      setIsEditing(false);
    } catch (err) {
      setEditError(err.response?.data?.error || "Failed to update profile details.");
    } finally {
      setSaving(false);
    }
  };

  // Reset password state
  const [showPwReset, setShowPwReset] = useState(false);
  const [newPw, setNewPw] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [pwLoading, setPwLoading] = useState(false);
  const [pwMsg, setPwMsg] = useState("");
  const [pwErr, setPwErr] = useState("");

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setPwErr(""); setPwMsg("");
    if (newPw.length < 4) { setPwErr("Password must be at least 4 characters."); return; }
    setPwLoading(true);
    try {
      await resetEmployeePassword(emp.employee_id, newPw);
      setPwMsg("Password updated successfully!");
      setNewPw("");
      setTimeout(() => { setPwMsg(""); setShowPwReset(false); }, 2500);
    } catch (err) {
      setPwErr(err.response?.data?.error || "Failed to update password.");
    } finally { setPwLoading(false); }
  };

  if (!emp) return null;


  const myTasks = emp.tasks || [];
  const CATEGORY_EMOJI = { Travel: "🚌", Food: "🍽️", Accommodation: "🏨", Other: "📎" };

  return (
    <div>
      {/* Back header */}
      <button onClick={onBack} style={{
        display: "flex", alignItems: "center", gap: 8, border: "none",
        background: "none", color: TOKENS.navyDeep, cursor: "pointer",
        marginBottom: 14, fontWeight: 700, fontSize: 13,
      }}>
        <ArrowLeft size={18} /> Back to team
      </button>

      {/* Employee hero */}
      <Card style={{
        background: `linear-gradient(135deg, ${TOKENS.navyDeep}, ${TOKENS.navySoft})`,
        padding: "20px 18px",
      }}>
        <div style={{ display: "flex", gap: 14, alignItems: "center" }}>
          <div style={{
            width: 60, height: 60, borderRadius: "50%",
            border: `2.5px solid ${TOKENS.gold}`,
            background: TOKENS.navyMid, overflow: "hidden",
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: 18, fontWeight: 700, color: TOKENS.gold, flexShrink: 0,
          }}>
            {emp.selfie
              ? <img src={getImageUrl(emp.selfie)} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
              : emp.initials
            }
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontFamily: "Fraunces, serif", fontSize: 18, fontWeight: 700, color: "#fff" }}>{emp.name}</div>
            <div style={{ fontSize: 12, color: "#9FB0C9", marginTop: 2 }}>{emp.role} · {emp.teamName}</div>
            <div style={{ display: "flex", gap: 8, marginTop: 8, flexWrap: "wrap", alignItems: "center" }}>
              {(() => { const { status, checkOutTime } = getEmpStatus(emp); return <StatusPill status={status} checkOutTime={checkOutTime} />; })()}
              <span style={{ fontSize: 12, color: "#9FB0C9", alignSelf: "center" }}>
                {formatClientDisplayTime(emp.checkedIn ? emp.checkInTime : (emp.checkOutTime || emp.lastSeen))}
              </span>
              {canMarkAbsent(emp) && !isMarkedAbsentToday(emp) && (
                <button
                  onClick={async () => {
                    if (!confirm(`Mark ${emp.name} as Absent for today?`)) return;
                    try {
                      await markEmployeeAbsent(emp.employee_id || emp.employeeId || emp.id);
                    } catch (err) {
                      alert(err.response?.data?.error || "Failed to mark absent.");
                    }
                  }}
                  style={{
                    padding: "4px 10px", borderRadius: 20, fontSize: 11, fontWeight: 700,
                    background: "rgba(255,255,255,0.12)", color: "#FECACA",
                    border: "1px solid rgba(248,113,113,0.4)", cursor: "pointer",
                    display: "inline-flex", alignItems: "center", gap: 4,
                  }}
                >
                  <UserX size={12} /> Mark Absent
                </button>
              )}
            </div>
          </div>
          <PerformanceGauge score={emp.score} size={72} label="Score" />
        </div>

        <div style={{ display: "flex", gap: 4, marginTop: 14, background: "rgba(255,255,255,0.07)", borderRadius: 8, padding: 3 }}>
          {SUB_TABS.map((t) => (
            <button key={t} onClick={() => setSub(t)} style={{
              flex: 1, border: "none", borderRadius: 6, padding: "6px 4px",
              fontSize: 10.5, fontWeight: 700, cursor: "pointer",
              background: sub === t ? TOKENS.gold : "transparent",
              color: sub === t ? TOKENS.navyDeep : "#9FB0C9",
              whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
            }}>{t}</button>
          ))}
        </div>
      </Card>

      {/* Profile sub-tab */}
      {sub === "Profile" && (
        <div>
          {editSuccess && (
            <div style={{ margin: "8px 0", padding: "8px 12px", background: TOKENS.successBg, border: `1px solid ${TOKENS.success}`, color: TOKENS.success, borderRadius: 8, fontSize: 12, fontWeight: 700 }}>
              ✓ {editSuccess}
            </div>
          )}
          {editError && (
            <div style={{ margin: "8px 0", padding: "8px 12px", background: TOKENS.dangerBg, border: `1px solid ${TOKENS.danger}`, color: TOKENS.danger, borderRadius: 8, fontSize: 12, fontWeight: 700 }}>
              ⚠️ {editError}
            </div>
          )}

          <SectionLabel
            right={
              <button
                onClick={handleOpenEdit}
                style={{
                  border: "none", background: isEditing ? TOKENS.dangerBg : `${TOKENS.navyDeep}12`,
                  color: isEditing ? TOKENS.danger : TOKENS.navyDeep,
                  borderRadius: 12, padding: "4px 10px", fontSize: 11, fontWeight: 700,
                  display: "inline-flex", alignItems: "center", gap: 4, cursor: "pointer",
                }}
              >
                {isEditing ? <><X size={12} /> Cancel</> : <><Edit3 size={12} /> Edit Details</>}
              </button>
            }
          >
            Personal Details
          </SectionLabel>

          {isEditing ? (
            <form onSubmit={handleSaveEmpDetails}>
              <Card style={{ padding: 16, marginBottom: 16 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: TOKENS.navyDeep, marginBottom: 12 }}>
                  ✏️ Edit Employee Personal Details
                </div>
                {[
                  { label: "Father's Name", field: "fatherName", placeholder: "Father's name" },
                  { label: "Mother's Name", field: "motherName", placeholder: "Mother's name" },
                  { label: "Date of Birth", field: "dob", type: "date" },
                  { label: "Blood Group",   field: "bloodGroup", isSelect: true },
                  { label: "Email Address", field: "email", placeholder: "name@company.com", type: "email" },
                  { label: "PAN Number", field: "panNo", placeholder: "ABCDE1234F" },
                  { label: "IFSC Code", field: "uanNo", placeholder: "IFSC Code" },
                  { label: "PF Number", field: "pfNo", placeholder: "PF Number" },
                  { label: "ESI Number", field: "esiNo", placeholder: "ESI Number" },
                  { label: "Bank Account No", field: "pranNo", placeholder: "Bank Account No" },
                  { label: "Base Salary", field: "baseSalary", placeholder: "Enter Amount", type: "number" },
                ].map(({ label, field, placeholder, type, isSelect }) => (
                  <div key={field} style={{ marginBottom: 10 }}>
                    <label style={{ fontSize: 11, fontWeight: 700, color: TOKENS.muted, display: "block", marginBottom: 4 }}>
                      {label}
                    </label>
                    {isSelect ? (
                      <select
                        value={editForm[field]}
                        onChange={e => setEditForm(f => ({ ...f, [field]: e.target.value }))}
                        style={{
                          width: "100%", padding: "9px 12px", borderRadius: 10,
                          border: `1.5px solid ${TOKENS.border}`, fontSize: 13, color: TOKENS.ink,
                          outline: "none", background: "#fff",
                        }}
                      >
                        <option value="">Select Blood Group</option>
                        {["A+", "B+", "O+", "AB+", "A-", "B-", "O-", "AB-", "A1+", "A1-", "A2+", "A2-", "A1B+", "A1B-", "A2B+", "A2B-"].map(bg => (
                          <option key={bg} value={bg}>{bg}</option>
                        ))}
                      </select>
                    ) : (
                      <input
                        type={type || "text"}
                        placeholder={placeholder}
                        value={editForm[field]}
                        onChange={e => setEditForm(f => ({ ...f, [field]: e.target.value }))}
                        style={{
                          width: "100%", padding: "9px 12px", borderRadius: 10,
                          border: `1.5px solid ${TOKENS.border}`, fontSize: 13, color: TOKENS.ink,
                          outline: "none", background: "#fff",
                        }}
                      />
                    )}
                  </div>
                ))}

                <div style={{ marginBottom: 14 }}>
                  <WhatsAppPhoneField
                    label="PHONE / WHATSAPP"
                    value={editForm.phone}
                    onChange={(phone) => setEditForm((f) => ({ ...f, phone }))}
                    onWhatsApp={(yes) => setEditForm((f) => ({ ...f, hasWhatsapp: yes }))}
                    onStatus={setWaCheckStatus}
                  />
                </div>

                <button
                  type="submit"
                  disabled={saving}
                  style={{
                    marginTop: 14, width: "100%", background: TOKENS.navyDeep, color: "#fff",
                    border: "none", borderRadius: 12, padding: "12px",
                    fontWeight: 700, fontSize: 13.5, cursor: saving ? "wait" : "pointer",
                    display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
                    opacity: saving ? 0.7 : 1,
                  }}
                >
                  {saving ? "Saving Changes…" : <><Save size={16} /> Save Employee Details</>}
                </button>
              </Card>
            </form>
          ) : (
            <Card style={{ padding: "0 16px" }}>
              <InfoRow label="Full Name"     value={emp.name} />
              <InfoRow label="Father's Name" value={emp.fatherName || emp.father_name} />
              <InfoRow label="Mother's Name" value={emp.motherName || emp.mother_name} />
              <InfoRow label="Date of Birth" value={emp.dob} />
              <InfoRow label="Blood Group"   value={emp.bloodGroup || emp.blood_group} color={TOKENS.danger} />
              <InfoRow 
                label="Phone" 
                value={
                  emp.phone ? (
                    <span style={{ display: "inline-flex", alignItems: "center", gap: 6, justifyContent: "flex-end" }}>
                      {emp.phone}
                      {employeeHasWhatsApp(emp) ? (
                        <span style={{ background: "#25D366", color: "#fff", fontSize: 9, padding: "2px 6px", borderRadius: 999, fontWeight: 800 }}>WA</span>
                      ) : (
                        <span style={{ background: TOKENS.danger, color: "#fff", fontSize: 9, padding: "2px 6px", borderRadius: 999, fontWeight: 800 }}>No WA</span>
                      )}
                    </span>
                  ) : "—"
                } 
              />
              <InfoRow label="Email"         value={emp.email} />
            </Card>
          )}

          <SectionLabel>Payroll & Compliance Details</SectionLabel>
          <Card style={{ padding: "0 16px" }}>
            <InfoRow label="PAN Number"   value={emp.panNo || emp.pan_no} />
            <InfoRow label="IFSC Code"   value={emp.uanNo || emp.uan_no} />
            <InfoRow label="PF Number"    value={emp.pfNo || emp.pf_no} />
            <InfoRow label="ESI Number"   value={emp.esiNo || emp.esi_no} />
            <InfoRow label="Bank Account No"  value={emp.pranNo || emp.pran_no} />
            <InfoRow label="Base Salary"  value={emp.baseSalary || emp.base_salary ? `₹${Number(emp.baseSalary || emp.base_salary).toLocaleString("en-IN")}` : ""} />
          </Card>

          <SectionLabel>Emergency Contact</SectionLabel>
          <Card style={{ padding: "0 16px" }}>
            <InfoRow label="Name"         value={emp.emergencyContact?.name} />
            <InfoRow label="Relationship" value={emp.emergencyContact?.relationship} />
            <InfoRow label="Phone"        value={emp.emergencyContact?.phone} color={TOKENS.danger} />
          </Card>

          <SectionLabel>Job Details</SectionLabel>
          <Card style={{ padding: "0 16px" }}>
            <InfoRow label="Employee ID" value={emp.employeeId || emp.employee_id} />
            <InfoRow label="Client"      value={emp.clientName || emp.client_name} />
            <InfoRow label="Team"        value={emp.teamName || emp.team_name} />
            <InfoRow label="Joined"      value={emp.joiningDate || emp.joining_date} />
          </Card>

          {/* Aadhaar KYC Upload for Manager */}
          <SectionLabel>KYC Documents — Aadhaar (Upload & Manage)</SectionLabel>
          <Card>
            <div style={{ display: "flex", gap: 12 }}>
              {["front", "back"].map((side) => {
                const imgUrl = getImageUrl(emp.aadhaar?.[side]);
                return (
                  <div key={side} style={{ flex: 1 }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: TOKENS.muted, letterSpacing: 0.5, marginBottom: 6 }}>
                      AADHAAR {side.toUpperCase()}
                    </div>
                    <label style={{
                      width: "100%", aspectRatio: "3/2", borderRadius: 12,
                      border: `2px dashed ${TOKENS.navyDeep}`, background: TOKENS.cream,
                      display: "flex", flexDirection: "column", alignItems: "center",
                      justifyContent: "center", overflow: "hidden", cursor: "pointer",
                      position: "relative",
                    }}>
                      {imgUrl ? (
                        <>
                          <img src={imgUrl} alt={`Aadhaar ${side}`} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                          <button
                            type="button"
                            onClick={async (e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              if (!confirm(`Are you sure you want to delete the Aadhaar ${side} document?`)) return;
                              try {
                                await api.delete(`/employees/${emp.employee_id || emp.id}/photo?type=aadhaar_${side}`);
                                await refreshTeam();
                              } catch (err) {
                                console.error("Error deleting Aadhaar document:", err);
                              }
                            }}
                            style={{
                              position: "absolute", top: 8, right: 8,
                              background: "rgba(239, 68, 68, 0.9)", color: "#fff",
                              border: "none", borderRadius: "50%", width: 24, height: 24,
                              display: "flex", alignItems: "center", justifyContent: "center",
                              cursor: "pointer", zIndex: 10, fontSize: 12, fontWeight: "bold",
                              boxShadow: "0 2px 6px rgba(0,0,0,0.15)"
                            }}
                            title="Delete document"
                          >
                            ✕
                          </button>
                          <div style={{
                            position: "absolute", bottom: 0, inset: "auto 0 0 0", background: "rgba(10,25,50,0.8)",
                            color: "#fff", padding: "4px 0", textAlign: "center", fontSize: 10, fontWeight: 700
                          }}>
                            📷 Click to Change
                          </div>
                        </>
                      ) : (
                        <div style={{ textAlign: "center", padding: 8 }}>
                          <Upload size={18} color={TOKENS.navyDeep} style={{ margin: "0 auto 4px" }} />
                          <span style={{ fontSize: 11.5, color: TOKENS.navyDeep, fontWeight: 700 }}>Upload {side}</span>
                        </div>
                      )}
                      <input
                        type="file"
                        accept="image/*"
                        style={{ display: "none" }}
                        onChange={async (e) => {
                          const file = e.target.files?.[0];
                          if (!file) return;
                          try {
                            const fd = new FormData();
                            fd.append("file", file);
                            fd.append("side", side);
                            await api.post(`/employees/${emp.employee_id || emp.id}/aadhaar`, fd, {
                              headers: { "Content-Type": "multipart/form-data" }
                            });
                            await refreshTeam();
                          } catch (err) {
                            console.error("Aadhaar upload error:", err);
                          }
                        }}
                      />
                    </label>
                  </div>
                );
              })}
            </div>
          </Card>

          {/* Attachments section */}
          <SectionLabel right={
            <AttachmentUploadBtn empId={emp.employee_id || emp.id} onDone={refreshTeam} />
          }>Attachments</SectionLabel>
          {(emp.attachments || []).length === 0 && (
            <Card style={{ textAlign: "center", color: TOKENS.muted, fontSize: 12, padding: 16 }}>No attachments yet.</Card>
          )}
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {(emp.attachments || []).map(att => {
              const fileUrl = getImageUrl(att.url);
              const isImg = fileUrl?.match(/\.(jpg|jpeg|png|gif|webp)$/i);
              return (
              <Card key={att.id} style={{ padding: 12 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  {isImg ? (
                    <img src={fileUrl} alt="attachment" style={{ width: 54, height: 54, objectFit: "cover", borderRadius: 8, border: `1px solid ${TOKENS.border}` }} />
                  ) : (
                    <div style={{ width: 54, height: 54, borderRadius: 8, border: `1px solid ${TOKENS.border}`, display: "flex", alignItems: "center", justifyContent: "center", background: "#f8f9fa", color: TOKENS.muted }}>
                      <FileText size={24} />
                    </div>
                  )}
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: TOKENS.ink }}>{att.caption || "Attachment"}</div>
                    <div style={{ fontSize: 11, color: TOKENS.muted }}>{att.created_at?.slice(0, 10)}</div>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                    <a href={getImageUrl(att.url)} target="_blank" rel="noopener noreferrer" style={{ fontSize: 11, color: TOKENS.navyDeep, display: "flex", alignItems: "center", gap: 4, textDecoration: "none", fontWeight: 700 }}>
                      <ExternalLink size={12} /> View
                    </a>
                    <button
                      onClick={async () => {
                        if (!confirm("Are you sure you want to delete this attachment?")) return;
                        try {
                          await api.delete(`/employees/${emp.employee_id || emp.id}/attachments/${att.id}`);
                          await refreshTeam();
                        } catch (err) {
                          console.error("Error deleting attachment:", err);
                        }
                      }}
                      style={{
                        background: "none", border: "none", color: TOKENS.danger,
                        fontSize: 11, fontWeight: 700, cursor: "pointer",
                        display: "flex", alignItems: "center", gap: 4, padding: 0
                      }}
                    >
                      <Trash2 size={12} /> Delete
                    </button>
                  </div>
                </div>
              </Card>
            )})}
          </div>

          {/* Reset Password */}
          <SectionLabel>Login Access</SectionLabel>
          <Card style={{ padding: "14px 16px" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <div style={{
                  width: 34, height: 34, borderRadius: 10,
                  background: `${TOKENS.navyDeep}12`,
                  display: "flex", alignItems: "center", justifyContent: "center",
                }}>
                  <KeyRound size={16} color={TOKENS.navyDeep} />
                </div>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: TOKENS.ink }}>Login Password</div>
                  <div style={{ fontSize: 11, color: TOKENS.muted }}>Set or reset employee portal access</div>
                </div>
              </div>
              <button
                onClick={() => { setShowPwReset(v => !v); setPwErr(""); setPwMsg(""); setNewPw(""); }}
                style={{
                  padding: "7px 14px", borderRadius: 10, border: `1.5px solid ${TOKENS.navyDeep}`,
                  background: showPwReset ? TOKENS.navyDeep : "#fff",
                  color: showPwReset ? "#fff" : TOKENS.navyDeep,
                  fontSize: 12, fontWeight: 700, cursor: "pointer",
                }}
              >
                {showPwReset ? "Cancel" : "Set Password"}
              </button>
            </div>

            {showPwReset && (
              <form onSubmit={handleResetPassword} style={{ marginTop: 14, borderTop: `1px solid ${TOKENS.border}`, paddingTop: 14 }}>
                <label style={{ fontSize: 11, fontWeight: 700, color: TOKENS.muted, letterSpacing: 0.5, display: "block", marginBottom: 6 }}>
                  NEW PASSWORD FOR {emp.name.toUpperCase()}
                </label>
                <div style={{ position: "relative", marginBottom: 12 }}>
                  <input
                    type={showPw ? "text" : "password"}
                    value={newPw}
                    onChange={e => setNewPw(e.target.value)}
                    placeholder="Enter new password (min. 4 chars)"
                    style={{
                      width: "100%", padding: "10px 40px 10px 12px", borderRadius: 10,
                      border: `1.5px solid ${TOKENS.border}`, fontSize: 13.5,
                      outline: "none", color: TOKENS.ink, background: TOKENS.cream,
                    }}
                  />
                  <button type="button" onClick={() => setShowPw(v => !v)} style={{
                    position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)",
                    border: "none", background: "none", cursor: "pointer", padding: 0,
                  }}>
                    {showPw ? <EyeOff size={16} color={TOKENS.muted} /> : <Eye size={16} color={TOKENS.muted} />}
                  </button>
                </div>

                {pwErr && <div style={{ fontSize: 12, color: TOKENS.danger, fontWeight: 600, marginBottom: 10 }}>⚠️ {pwErr}</div>}
                {pwMsg && <div style={{ fontSize: 12, color: TOKENS.success, fontWeight: 600, marginBottom: 10 }}>✓ {pwMsg}</div>}

                <button
                  type="submit"
                  disabled={pwLoading}
                  style={{
                    width: "100%", padding: "10px 14px", borderRadius: 10,
                    border: "none", background: TOKENS.navyDeep, color: "#fff",
                    fontWeight: 700, fontSize: 13, cursor: "pointer",
                    opacity: pwLoading ? 0.7 : 1,
                  }}
                >
                  {pwLoading ? "Updating…" : "Confirm Password"}
                </button>
              </form>
            )}

            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", borderTop: `1px solid ${TOKENS.border}`, marginTop: 12, paddingTop: 12 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <div style={{
                  width: 34, height: 34, borderRadius: 10,
                  background: emp.is_disabled ? `${TOKENS.danger}12` : `${TOKENS.success}12`,
                  display: "flex", alignItems: "center", justifyContent: "center",
                }}>
                  {emp.is_disabled ? <UserX size={16} color={TOKENS.danger} /> : <UserCheck size={16} color={TOKENS.success} />}
                </div>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: TOKENS.ink }}>Authorization Status</div>
                  <div style={{ fontSize: 11, color: TOKENS.muted }}>
                    {emp.is_disabled ? "Login access is currently DISABLED" : "Login access is currently ENABLED"}
                  </div>
                </div>
              </div>
              <button
                onClick={async () => {
                  const action = emp.is_disabled ? "enable" : "disable";
                  if (!confirm(`Are you sure you want to ${action} login access for ${emp.name}?`)) return;
                  try {
                    await api.patch(`/employees/${emp.employee_id || emp.id}/status`, { disabled: !emp.is_disabled });
                    await refreshTeam();
                    alert(`Employee login access successfully ${emp.is_disabled ? "enabled" : "disabled"}.`);
                  } catch (err) {
                    console.error("Status toggle error:", err);
                    alert(err.response?.data?.error || "Failed to update authorization status.");
                  }
                }}
                style={{
                  padding: "7px 14px", borderRadius: 10,
                  border: `1.5px solid ${emp.is_disabled ? TOKENS.success : TOKENS.danger}`,
                  background: "#fff",
                  color: emp.is_disabled ? TOKENS.success : TOKENS.danger,
                  fontSize: 12, fontWeight: 700, cursor: "pointer",
                }}
              >
                {emp.is_disabled ? "Enable Access" : "Disable Access"}
              </button>
            </div>
          </Card>

          {/* Danger Zone */}
          <SectionLabel style={{ marginTop: 24, color: TOKENS.danger }}>Danger Zone</SectionLabel>
          <Card style={{ padding: "14px 16px", border: `1.5px solid ${TOKENS.danger}40` }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div>
                <div style={{ fontSize: 13, fontWeight: 700, color: TOKENS.danger }}>Delete Employee</div>
                <div style={{ fontSize: 11, color: TOKENS.muted, marginTop: 2 }}>Permanently delete employee account, attendance, logs, and all records.</div>
              </div>
              <button
                onClick={async () => {
                  if (!confirm(`⚠️ WARNING: Are you sure you want to permanently delete employee ${emp.name}?\n\nThis will delete all their tasks, checkins, leaves, and daily reports from the database. This action CANNOT be undone.`)) return;
                  if (!confirm(`Final Confirmation: Delete ${emp.name} permanently?`)) return;
                  try {
                    await api.delete(`/employees/${emp.employee_id || emp.id}`);
                    alert("Employee successfully deleted from database.");
                    if (onBack) onBack();
                    await refreshTeam();
                  } catch (err) {
                    console.error("Delete employee error:", err);
                    alert(err.response?.data?.error || "Failed to delete employee.");
                  }
                }}
                style={{
                  padding: "8px 16px", borderRadius: 10, border: "none",
                  background: TOKENS.danger, color: "#fff",
                  fontSize: 12, fontWeight: 700, cursor: "pointer",
                  boxShadow: "0 2px 8px rgba(239,68,68,0.2)"
                }}
              >
                Delete Employee
              </button>
            </div>
          </Card>
        </div>
      )}

      {/* GPS Location sub-tab */}
      {sub === "GPS Location" && (
        <div>
          <SectionLabel>Live Employee Location</SectionLabel>
          <Card style={{ padding: 18, background: TOKENS.cream }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
              <div>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <MapPin size={20} color={emp.checkedIn ? TOKENS.success : TOKENS.navyDeep} />
                  <span style={{ fontFamily: "Fraunces, serif", fontSize: 17, fontWeight: 700, color: TOKENS.navyDeep }}>
                    {emp.checkInLocation?.city || emp.lastLocation || "Unknown Location"}
                  </span>
                </div>
                <div style={{ fontSize: 12, color: TOKENS.muted, marginTop: 4 }}>
                  Status: <strong style={{ color: emp.checkedIn ? TOKENS.success : TOKENS.danger }}>
                    {emp.checkedIn ? `Checked In (${formatClientDisplayTime(emp.checkInTime || emp.lastSeen)})` : emp.onOD ? `On OD (${emp.odCity})` : `Not Checked In (Last seen ${formatClientDisplayTime(emp.lastSeen)})`}
                  </strong>
                </div>
              </div>
              {(() => { const { status, checkOutTime } = getEmpStatus(emp); return <StatusPill status={status} checkOutTime={checkOutTime} />; })()}
            </div>

            {/* Coordinates display */}
            {(emp.checkInLocation?.lat || emp.check_in_lat) ? (
              <div style={{
                marginTop: 14, background: "#fff", borderRadius: 12, padding: "12px 14px",
                border: `1px solid ${TOKENS.border}`
              }}>
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, marginBottom: 6 }}>
                  <span style={{ color: TOKENS.muted }}>Latitude</span>
                  <span style={{ fontWeight: 700, color: TOKENS.ink, fontFamily: "monospace" }}>
                    {(emp.checkInLocation?.lat || emp.check_in_lat).toFixed(6)}° N
                  </span>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, marginBottom: 6 }}>
                  <span style={{ color: TOKENS.muted }}>Longitude</span>
                  <span style={{ fontWeight: 700, color: TOKENS.ink, fontFamily: "monospace" }}>
                    {(emp.checkInLocation?.lng || emp.check_in_lng).toFixed(6)}° E
                  </span>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12 }}>
                  <span style={{ color: TOKENS.muted }}>GPS Source</span>
                  <span style={{ fontWeight: 700, color: TOKENS.success }}>Real-time Device GPS</span>
                </div>
              </div>
            ) : (
              <div style={{ marginTop: 12, fontSize: 12, color: TOKENS.muted }}>
                📍 Location Region: {emp.lastLocation || "Western Region"}
              </div>
            )}

            {/* Map Link Button */}
            <a
              href={
                (emp.checkInLocation?.lat || emp.check_in_lat)
                  ? `https://www.google.com/maps?q=${emp.checkInLocation?.lat || emp.check_in_lat},${emp.checkInLocation?.lng || emp.check_in_lng}`
                  : `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent((emp.lastLocation || "Mumbai") + ", India")}`
              }
              target="_blank"
              rel="noopener noreferrer"
              style={{
                marginTop: 14, width: "100%", background: TOKENS.navyDeep, color: "#fff",
                borderRadius: 12, padding: "12px 16px", textDecoration: "none",
                fontWeight: 700, fontSize: 13, display: "flex", alignItems: "center",
                justifyContent: "center", gap: 8, boxShadow: `0 4px 12px ${TOKENS.navyDeep}25`
              }}
            >
              <ExternalLink size={16} /> Track on Google Maps
            </a>
          </Card>

          {/* Location History Log */}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <SectionLabel>Check-In Location History</SectionLabel>
            <button 
              onClick={() => setShowRegModal(true)}
              style={{ background: TOKENS.navyDeep, color: "#fff", border: "none", borderRadius: 8, padding: "6px 12px", fontSize: 11, fontWeight: 700, cursor: "pointer", display: "flex", alignItems: "center", gap: 4 }}
            >
              <Clock size={12} /> Regularize Missing GPS
            </button>
          </div>
          {(() => {
            const historyList = [...(emp.checkinsHistory || [])].sort((a, b) => {
              if (b.id && a.id) return b.id - a.id;
              return 0;
            });
            if (historyList.length === 0) {
              return (
                <Card style={{ textAlign: "center", color: TOKENS.muted, fontSize: 12, padding: 16 }}>
                  No check-in logs recorded today.
                </Card>
              );
            }
            return (
              <Card style={{ padding: 0 }}>
                {historyList.map((c, idx) => (
                  <div key={c.id || idx} style={{
                    display: "flex", alignItems: "center", justifyContent: "space-between",
                    padding: "12px 16px", borderBottom: idx < historyList.length - 1 ? `1px solid ${TOKENS.border}` : "none"
                  }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <div style={{
                        width: 32, height: 32, borderRadius: 10,
                        background: c.type === "in" ? TOKENS.successBg : TOKENS.dangerBg,
                        display: "flex", alignItems: "center", justifyContent: "center"
                      }}>
                        <MapPin size={16} color={c.type === "in" ? TOKENS.success : TOKENS.danger} />
                      </div>
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 700, color: TOKENS.ink }}>
                          {c.type === "in" ? "Check In" : "Check Out"} · {c.city || "Live GPS"}
                        </div>
                        <div style={{ fontSize: 11, color: TOKENS.muted, marginTop: 2 }}>
                          {formatClientDisplayTime(c.timestamp || c.date)} {c.lat ? `· ${c.lat.toFixed(4)}, ${c.lng.toFixed(4)}` : ""}
                        </div>
                      </div>
                    </div>
                    {c.lat && (
                      <a
                        href={`https://www.google.com/maps?q=${c.lat},${c.lng}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          padding: "5px 10px", borderRadius: 8, background: TOKENS.cream,
                          border: `1px solid ${TOKENS.border}`, color: TOKENS.navyDeep,
                          fontSize: 11, fontWeight: 700, textDecoration: "none",
                          display: "flex", alignItems: "center", gap: 4
                        }}
                      >
                        <ExternalLink size={12} /> Map
                      </a>
                    )}
                  </div>
                ))}
              </Card>
            );
          })()}
        </div>
      )}

      {/* OD History sub-tab */}
      {sub === "OD History" && (
        <div>
          <SectionLabel>OD trips</SectionLabel>
          {(emp.odHistory || []).length === 0 && (
            <Card style={{ textAlign: "center", color: TOKENS.muted, fontSize: 13, padding: 20 }}>No OD trips yet.</Card>
          )}
          <div style={{ display: "flex", flexDirection: "column", gap: 10, marginTop: 4 }}>
            {(emp.odHistory || []).map((od) => {
              const todayStr = new Date().toLocaleDateString("en-CA", { timeZone: "Asia/Kolkata" });
              const isCompleted = od.isCompleted || (od.to && todayStr > od.to);
              const statusKey = od.statusKey || (isCompleted ? "od-completed" : od.arrived ? "arrived" : "od-active");

              return (
                <Card key={od.id} style={{ padding: 14 }}>
                  <div style={{ display: "flex", justifyContent: "space-between" }}>
                    <div>
                      <div style={{ fontWeight: 700, fontSize: 14 }}>📍 {od.city}</div>
                      <div style={{ fontSize: 12, color: TOKENS.muted, marginTop: 2 }}>
                        {od.client} · {od.from} → {od.to}
                      </div>
                    </div>
                    <StatusPill status={statusKey} />
                  </div>
                  {od.completed || od.completed_time || isCompleted ? (
                    <div style={{
                      marginTop: 8, display: "flex", alignItems: "center", gap: 6,
                      background: TOKENS.successBg, borderRadius: 8, padding: "6px 10px",
                      fontSize: 12, color: TOKENS.success, fontWeight: 600,
                    }}>
                      <CheckCircle2 size={13} /> OD Over / Completed at {od.completedTime || od.completed_time || od.to} ✓
                    </div>
                  ) : od.arrived ? (
                    <div style={{
                      marginTop: 8, display: "flex", alignItems: "center", gap: 6,
                      background: TOKENS.successBg, borderRadius: 8, padding: "6px 10px",
                      fontSize: 12, color: TOKENS.success, fontWeight: 600,
                    }}>
                      <CheckCircle2 size={13} /> {od.arrivalLocation} · {formatClientDisplayTime(od.arrivalTime)}
                    </div>
                  ) : null}

                  {/* Attached Project / OD Photos */}
                  {((od.photos && od.photos.length > 0) || od.photo_url || od.photoUrl) && (
                    <div style={{ marginTop: 10, borderTop: `1px dashed ${TOKENS.border}`, paddingTop: 8 }}>
                      <div style={{ fontSize: 11, fontWeight: 700, color: TOKENS.navyDeep, marginBottom: 6, display: "flex", alignItems: "center", gap: 4 }}>
                        📷 Attached OD Project Photos ({od.photos?.length || 1})
                      </div>
                      <div style={{ display: "flex", gap: 8, overflowX: "auto", paddingBottom: 4 }}>
                        {(od.photos || [{ url: od.photoUrl || od.photo_url, caption: "Project Photo" }]).map((p, pIdx) => {
                          const imgUrl = getImageUrl(p.url);
                          return (
                            <div key={p.id || pIdx} style={{ flexShrink: 0 }}>
                              <a href={imgUrl} target="_blank" rel="noopener noreferrer" style={{ textDecoration: "none" }}>
                                <img
                                  src={imgUrl}
                                  alt={p.caption || "OD Photo"}
                                  style={{ width: 72, height: 72, borderRadius: 10, objectFit: "cover", border: `1.5px solid ${TOKENS.border}` }}
                                />
                              </a>
                              {p.caption && (
                                <div style={{ fontSize: 10, color: TOKENS.muted, marginTop: 2, maxWidth: 72, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                  {p.caption}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </Card>
              );
            })}
          </div>
        </div>
      )}

      {/* Performance sub-tab */}
      {sub === "Performance" && (
        <div>
          {/* Performance Index Banner */}
          <Card style={{ background: `linear-gradient(135deg, ${TOKENS.navyDeep}, ${TOKENS.navySoft})`, padding: 18, marginBottom: 0 }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <div>
                <div style={{ fontSize: 11, fontWeight: 700, color: "#9FB0C9", letterSpacing: 0.5 }}>PERFORMANCE INDEX</div>
                <div style={{ fontFamily: "Fraunces, serif", fontSize: 36, fontWeight: 700, color: TOKENS.gold, lineHeight: 1.1 }}>
                  {emp.performanceIndex ?? emp.score ?? "—"}%
                </div>
                <div style={{ fontSize: 12, color: "#9FB0C9", marginTop: 4 }}>Standard Weighted Operations Rating</div>
              </div>
              <div style={{ textAlign: "center" }}>
                {(emp.performanceIndex ?? emp.score ?? 0) >= 90 ? (
                  <div style={{ background: TOKENS.successBg, border: `1px solid ${TOKENS.success}`, borderRadius: 12, padding: "6px 12px", fontSize: 11, fontWeight: 700, color: TOKENS.success, display: "flex", alignItems: "center", gap: 6 }}>
                    <Star size={13} /> Benefits Eligible
                  </div>
                ) : (
                  <div style={{ background: `${TOKENS.danger}20`, border: `1px solid ${TOKENS.danger}44`, borderRadius: 12, padding: "6px 12px", fontSize: 11, fontWeight: 700, color: TOKENS.danger }}>
                    Below 90% threshold
                  </div>
                )}
              </div>
            </div>
            {/* Factor breakdown */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8, marginTop: 14 }}>
              {[
                { label: "Attendance", icon: "📅", weight: "20% weight" },
                { label: "Punctuality", icon: "⏱️", weight: "10% weight" },
                { label: "Daily Reports", icon: "📋", weight: "15% weight" },
                { label: "Task Updates", icon: "🔄", weight: "20% weight" },
                { label: "OD Coverage", icon: "✈️", weight: "10% weight" },
                { label: "Task Done", icon: "✅", weight: "25% weight" },
              ].map(({ label, icon, weight }) => (
                <div key={label} style={{ background: "rgba(255,255,255,0.08)", borderRadius: 10, padding: "8px 10px" }}>
                  <div style={{ fontSize: 12 }}>{icon}</div>
                  <div style={{ fontSize: 10, color: "#9FB0C9", marginTop: 3 }}>{label}</div>
                  <div style={{ fontSize: 12, fontWeight: 700, color: "#fff", marginTop: 2 }}>{weight}</div>
                </div>
              ))}
            </div>
          </Card>
          <Card style={{ padding: "16px 8px 8px" }}>
            <ResponsiveContainer width="100%" height={120}>
              <LineChart data={emp.punctualityTrend || []}>
                <CartesianGrid vertical={false} stroke="#F0ECE1" />
                <XAxis dataKey="day" tick={{ fontSize: 10, fill: TOKENS.muted }} axisLine={false} tickLine={false} />
                <YAxis hide />
                <Tooltip contentStyle={{ fontFamily: "Inter", fontSize: 12, borderRadius: 10, border: `1px solid ${TOKENS.border}` }} />
                <Line type="monotone" dataKey="min" stroke={TOKENS.gold} strokeWidth={2.5} dot={{ r: 4, fill: TOKENS.navyDeep }} />
              </LineChart>
            </ResponsiveContainer>
            <div style={{ fontSize: 11, color: TOKENS.muted, textAlign: "center", marginTop: 4 }}>
              Minutes early (–) / late (+) relative to 9:00 AM shift
            </div>
          </Card>

          <SectionLabel>Tasks breakdown</SectionLabel>
          <Card style={{ padding: 8 }}>
            {myTasks.length === 0 ? (
              <div style={{ textAlign: "center", color: TOKENS.muted, fontSize: 12, padding: 14 }}>No tasks assigned.</div>
            ) : (
              myTasks.map((t, i) => (
                <div key={t.id} style={{
                  padding: "14px 12px",
                  borderBottom: i < myTasks.length - 1 ? `1px solid ${TOKENS.border}` : "none",
                }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10, justifyContent: "space-between" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      {t.status === "done"
                        ? <CheckCircle2 size={16} color={TOKENS.success} />
                        : <Clock size={16} color={TOKENS.muted} />
                      }
                      <div>
                        <div style={{ fontSize: 13.5, fontWeight: 700, color: TOKENS.ink }}>{t.title}</div>
                        <div style={{ fontSize: 11.5, color: TOKENS.muted, marginTop: 2 }}>{t.location}</div>
                      </div>
                    </div>
                    <StatusPill status={t.status} />
                  </div>

                  {/* Completion Report Display */}
                  {t.status === "done" && (t.completion_status || t.completion_remarks) && (
                    <div style={{
                      marginTop: 10,
                      padding: "10px 14px",
                      background: TOKENS.cream,
                      borderRadius: 10,
                      border: `1.5px solid ${TOKENS.border}`,
                      fontSize: 12,
                    }}>
                      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6, fontWeight: 600 }}>
                        <span>Report Status: <strong style={{ color: t.completion_status === "Completed" ? TOKENS.success : TOKENS.danger }}>{t.completion_status}</strong></span>
                        <span>Reporting Team: <strong>{t.completion_team}</strong></span>
                      </div>
                      <div style={{ color: TOKENS.ink, lineHeight: 1.4, marginBottom: 6 }}>
                        <strong>Completed Items:</strong> {t.completion_remarks}
                      </div>
                      {t.completion_lat && (
                        <div style={{ fontSize: 10.5, color: TOKENS.muted }}>
                          📍 GPS Verified: Lat {t.completion_lat.toFixed(5)}, Lng {t.completion_lng.toFixed(5)} · Completed: {formatClientDisplayTime(t.completed_at || t.time)}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Manager Evaluation Display */}
                  {t.status === "done" && (t.manager_rating || t.manager_feedback) && (
                    <div style={{
                      marginTop: 8,
                      padding: "10px 14px",
                      background: `${TOKENS.gold}12`,
                      borderRadius: 10,
                      border: `1.5px solid ${TOKENS.gold}40`,
                      fontSize: 12,
                    }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: t.manager_feedback ? 4 : 0 }}>
                        <span style={{ fontWeight: 700, color: TOKENS.navyDeep }}>Manager Review:</span>
                        <span style={{ color: TOKENS.gold, fontSize: 12, fontWeight: 700 }}>
                          {t.manager_rating ? "★".repeat(t.manager_rating) : ""} ({t.manager_rating || 0}/5)
                        </span>
                      </div>
                      {t.manager_feedback && (
                        <div style={{ color: TOKENS.ink, fontStyle: "italic", lineHeight: 1.4 }}>
                          "{t.manager_feedback}"
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))
            )}
          </Card>
        </div>
      )}

      {/* Work Reports sub-tab */}
      {sub === "Work Reports" && (
        <div>
          <SectionLabel>Daily Work Reports</SectionLabel>
          {loadingReports && <Card style={{ textAlign: "center", color: TOKENS.muted, fontSize: 13, padding: 20 }}>Loading reports…</Card>}
          {!loadingReports && workReports.length === 0 && (
            <Card style={{ textAlign: "center", color: TOKENS.muted, fontSize: 13, padding: 20 }}>
              <FileText size={28} color={TOKENS.border} style={{ margin: "0 auto 8px" }} />
              No work reports submitted yet.
            </Card>
          )}
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {workReports.map((r, idx) => (
              <Card key={r.id || idx} style={{ padding: 14, borderLeft: `4px solid ${TOKENS.gold}` }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 6 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: TOKENS.navyDeep }}>{r.date}</div>
                  <div style={{ background: `${TOKENS.gold}20`, border: `1px solid ${TOKENS.gold}55`, borderRadius: 8, padding: "2px 8px", fontSize: 11, fontWeight: 700, color: TOKENS.navyDeep, display: "flex", alignItems: "center", gap: 4 }}>
                    <Clock size={11} /> {r.timeSpent || r.time_spent}
                  </div>
                </div>
                <div style={{ fontSize: 13, color: TOKENS.ink, lineHeight: 1.5, whiteSpace: "pre-wrap" }}>{r.work}</div>
                {r.remarks && (
                  <div style={{ fontSize: 11, color: TOKENS.muted, fontStyle: "italic", marginTop: 8, borderTop: `1px dashed ${TOKENS.border}`, paddingTop: 6 }}>
                    Remarks: {r.remarks}
                  </div>
                )}
              </Card>
            ))}
          </div>

          <div style={{ height: 24 }} />

          {/* Monthly Performance Reports */}
          <MonthlyPerformanceReports employeeId={emp?.employee_id || emp?.id} isManagerView={true} />
        </div>
      )}

      {/* Reimbursements sub-tab */}
      {sub === "Reimbursements" && (
        <div>
          <SectionLabel>All reimbursements</SectionLabel>
          {(emp.reimbursements || []).length === 0 && (
            <Card style={{ textAlign: "center", color: TOKENS.muted, fontSize: 13, padding: 20 }}>None submitted.</Card>
          )}
          <div style={{ display: "flex", flexDirection: "column", gap: 10, marginTop: 4 }}>
            {(emp.reimbursements || []).map((r) => (
              <Card key={r.id} style={{ padding: 14 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 13 }}>
                      {CATEGORY_EMOJI[r.category] || "📎"} {r.category}
                    </div>
                    <div style={{ fontSize: 12, color: TOKENS.muted, marginTop: 2 }}>{r.description}</div>
                    <div style={{ fontSize: 11, color: TOKENS.muted, marginTop: 2 }}>{r.date}</div>
                  </div>
                  <div style={{ textAlign: "right" }}>
                    <div style={{ fontFamily: "Fraunces, serif", fontSize: 16, fontWeight: 700, color: TOKENS.navyDeep }}>
                      {r.amount?.toLocaleString("en-IN")}
                    </div>
                    <StatusPill status={r.status} style={{ marginTop: 5, display: "inline-block" }} />
                    <button
                      onClick={() => {
                        const token = localStorage.getItem("fp_token");
                        window.open(`${getBackendOrigin()}/api/reimbursements/${r.id}/voucher?token=${encodeURIComponent(token || "")}`, "_blank");
                      }}
                      style={{
                        display: "flex", alignItems: "center", gap: 6, marginTop: 10, marginLeft: "auto",
                        background: "#fff", color: TOKENS.navyDeep, border: `1.5px solid ${TOKENS.border}`,
                        borderRadius: 8, padding: "6px 10px", fontSize: 11, fontWeight: 700, cursor: "pointer",
                      }}
                    >
                      📄 Download Reimbursement Form
                    </button>
                    {(r.receiptUrl || r.receipt) && (
                      <div style={{ marginTop: 6 }}>
                        {(r.receiptUrl || r.receipt)?.match(/\.(jpg|jpeg|png|gif|webp)$/i) || (r.receiptUrl || r.receipt)?.startsWith("data:image") ? (
                          <a href={getImageUrl(r.receiptUrl || r.receipt)} target="_blank" rel="noopener noreferrer" style={{ display: "block" }}>
                            <img src={getImageUrl(r.receiptUrl || r.receipt)} alt="Receipt" style={{ width: 44, height: 44, borderRadius: 8, objectFit: "cover", border: `1px solid ${TOKENS.border}`, margin: "4px 0 2px auto" }} />
                          </a>
                        ) : null}
                        <a href={getImageUrl(r.receiptUrl || r.receipt)} target="_blank" rel="noopener noreferrer" download style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: 11, fontWeight: 700, color: TOKENS.navyDeep, textDecoration: "none" }}>
                          <ExternalLink size={12} /> View Receipt
                        </a>
                      </div>
                    )}
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Incidents sub-tab */}
      {sub === "Incidents" && (
        <div>
          <SectionLabel>Registered Incidents</SectionLabel>
          {(emp.incidents || []).length === 0 && (
            <Card style={{ textAlign: "center", color: TOKENS.muted, fontSize: 13, padding: 20 }}>
              No incidents registered by this employee.
            </Card>
          )}
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {(emp.incidents || []).map((inc) => {
              const isSolved = inc.has_solution === 1 || inc.has_solution === true;
              return (
                <Card key={inc.id} style={{ padding: 14, borderLeft: `4px solid ${isSolved ? TOKENS.success : TOKENS.danger}` }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                    <div style={{ flex: 1, minWidth: 0, paddingRight: 10 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 6 }}>
                        <span style={{ fontWeight: 700, fontSize: 13.5, color: TOKENS.navyDeep }}>
                          {inc.department} Department
                        </span>
                        <span style={{ fontSize: 11, color: TOKENS.muted, display: "inline-flex", alignItems: "center", gap: 3 }}>
                          <Clock size={11} /> {formatDisplayDateTime(inc.incident_date)}
                        </span>
                      </div>

                      <div style={{ fontSize: 13, color: TOKENS.ink, lineHeight: 1.45, fontWeight: 500, whiteSpace: "pre-wrap" }}>
                        {inc.problem_statement}
                      </div>

                      {/* Roadblock details (if unsolved) */}
                      {!isSolved && inc.no_solution_reason && (
                        <div style={{ background: TOKENS.dangerBg, padding: 10, borderRadius: 10, border: `1px solid ${TOKENS.danger}30`, marginTop: 10 }}>
                          <div style={{ fontWeight: 700, color: TOKENS.danger, marginBottom: 4, fontSize: 11.5 }}>
                            ⚠️ Obstacle / Roadblock Details:
                          </div>
                          <div style={{ color: TOKENS.ink, lineHeight: 1.4, fontSize: 12 }}>{inc.no_solution_reason}</div>
                        </div>
                      )}

                      {/* Solution details (if solved) */}
                      {isSolved && (
                        <div style={{ background: TOKENS.successBg, padding: 12, borderRadius: 10, border: `1px solid ${TOKENS.success}30`, display: "flex", flexDirection: "column", gap: 8, marginTop: 10 }}>
                          <div>
                            <div style={{ fontWeight: 700, color: TOKENS.success, marginBottom: 4, fontSize: 11.5 }}>
                              ✓ Solved Details:
                            </div>
                            <div style={{ color: TOKENS.ink, lineHeight: 1.4, fontSize: 12 }}>{inc.solution}</div>
                          </div>

                          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, borderTop: `1px solid ${TOKENS.success}20`, paddingTop: 8, marginTop: 4, fontSize: 11 }}>
                            <div>
                              <span style={{ color: TOKENS.muted, fontWeight: 600 }}>Solved By:</span>
                              <div style={{ color: TOKENS.ink, fontWeight: 700, marginTop: 2 }}>
                                {inc.solved_by || "Unknown"}
                              </div>
                            </div>
                            <div>
                              <span style={{ color: TOKENS.muted, fontWeight: 600 }}>Total Time Taken:</span>
                              <div style={{ color: TOKENS.ink, fontWeight: 700, marginTop: 2 }}>
                                {inc.total_time_taken || "N/A"}
                              </div>
                            </div>
                          </div>

                          {inc.solution_date && (
                            <div style={{ fontSize: 11, color: TOKENS.muted, borderTop: `1px solid ${TOKENS.success}10`, paddingTop: 6 }}>
                              Solution resolved at: <strong>{formatDisplayDateTime(inc.solution_date)}</strong>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Photo Attachment */}
                      {inc.photoUrl && (
                        <div style={{ marginTop: 10 }}>
                          <div style={{ fontWeight: 700, color: TOKENS.navyDeep, marginBottom: 6, fontSize: 11.5 }}>📷 Incident Attachment:</div>
                          <a href={getImageUrl(inc.photoUrl)} target="_blank" rel="noopener noreferrer" style={{ display: "inline-block" }}>
                            <img
                              src={getImageUrl(inc.photoUrl)}
                              alt="Incident photo"
                              style={{ maxWidth: "100%", maxHeight: 160, borderRadius: 10, border: `1.5px solid ${TOKENS.border}`, objectFit: "contain" }}
                            />
                          </a>
                        </div>
                      )}
                    </div>

                    <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 8, flexShrink: 0 }}>
                      <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                        <span style={{ fontSize: 10.5, color: TOKENS.muted }}>Employee:</span>
                        <StatusPill status={isSolved ? "solved" : "unsolved"} />
                      </div>
                      
                      <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                        <span style={{ fontSize: 10.5, color: TOKENS.muted }}>Manager:</span>
                        <StatusPill status={inc.manager_status || "pending"} />
                      </div>

                      {(inc.manager_status || "pending") === "pending" && (
                        <div style={{ display: "flex", flexDirection: "column", gap: 6, marginTop: 10, minWidth: 130 }}>
                          <button
                            onClick={() => handleUpdateIncidentStatus(inc.id, "approved")}
                            style={{
                              background: TOKENS.success, color: "#fff", border: "none",
                              borderRadius: 6, padding: "5px 10px", fontSize: 11, fontWeight: 700,
                              cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 4
                            }}
                          >
                            Approve Completed
                          </button>
                          <button
                            onClick={() => handleUpdateIncidentStatus(inc.id, "rejected")}
                            style={{
                              background: TOKENS.danger, color: "#fff", border: "none",
                              borderRadius: 6, padding: "5px 10px", fontSize: 11, fontWeight: 700,
                              cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 4
                            }}
                          >
                            Disapprove
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        </div>
      )}

      {showRegModal && (
        <RegularizationModal 
          empId={emp.employeeId || emp.employee_id || emp.id} 
          onClose={() => setShowRegModal(false)} 
        />
      )}
    </div>
  );
}
